import { Component, OnInit } from '@angular/core'
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { of } from 'rxjs'
import { catchError, take, timeout } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { Document, DocumentType } from 'src/app/features/document/document'
import { documentActions } from 'src/app/features/document/document.actions'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import {
  HomePageButton,
  HomePageButtonStyle,
  HomePageConfigWebOption,
  HomePageLocalizedValue,
  HomePageSlide
} from 'src/app/shared/home-page.types'
import { normalizeNumber } from 'src/app/shared/utils/numbers'
import { WholeNumberValidator } from 'src/app/shared/validators/whole-number.validator'
import { environment } from 'src/environments/environment'
import { v4 as uuidv4 } from 'uuid'
import { ConfigOptionsKeys } from '../../../shared/constants'
import { configurationOptionActions } from '../configuration-option.actions'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { ConfigurationOptionService } from '../configuration-option.service'

type LanguageCode = 'nl' | 'en' | 'de' | 'fr'
type TitleKey = `title_${LanguageCode}`
type ImageUrlKey = `imageUrl_${LanguageCode}`
type AltTextKey = `altText_${LanguageCode}`
type TitleSource = Partial<Record<TitleKey, string | null | undefined>>
type SlideFormSource = Partial<
  Record<ImageUrlKey | AltTextKey, string | null | undefined>
> & {
  url?: string | null | undefined
  order?: number | null | undefined
}

@Component({
  selector: 'configuration-option-home-page-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionHomePageTabComponent implements OnInit {
  public form: UntypedFormGroup
  public titleCollapsed = false
  public featuredContentCollapsed = false
  public buttonsCollapsed = false
  public slideCollapsed: boolean[] = []
  public slideLanguageSelected: LanguageCode[] = []
  public buttonCollapsed: boolean[] = []
  public readonly maxSlides = 3
  public readonly languageCodes: LanguageCode[] = ['nl', 'en', 'de', 'fr']
  public readonly editAccessRights = [
    AccessRights.Settings_write,
    AccessRights.ConfigurationOptions_write
  ]
  public staticAssetBaseUrl = environment.staticAssetBaseUrl
  public readonly styleOptions = [
    {
      value: HomePageButtonStyle.PRIMARY,
      label: 'configurationOptionModule.primary'
    },
    {
      value: HomePageButtonStyle.SECONDARY,
      label: 'general.secondary'
    }
  ]
  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private configOptionService: ConfigurationOptionService,
    private notificationService: NotificationService,
    private actions$: Actions
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      ...this.buildTitleControls(null),
      slides: this.fb.array([]),
      buttons: this.fb.array([])
    })

    this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType<HomePageConfigWebOption>(
          ConfigOptionsKeys.HOME_PAGE_CONFIG
        )
      )
      .pipe(take(1))
      .subscribe((config) => {
        if (!config) {
          return
        }

        this.form.patchValue(this.buildTitleFormValue(config.title))
        const slides = [...(config.slides ?? [])].sort(
          (firstSlide, secondSlide) =>
            (normalizeNumber(firstSlide.order) ?? 0) -
            (normalizeNumber(secondSlide.order) ?? 0)
        )
        const buttons = [...(config.buttons ?? [])].sort(
          (firstButton, secondButton) =>
            (normalizeNumber(firstButton.order) ?? 0) -
            (normalizeNumber(secondButton.order) ?? 0)
        )
        slides.slice(0, this.maxSlides).forEach((slide) => this.addSlide(slide))
        buttons.forEach((button) => this.addButton(button))
      })
  }

  get slides(): UntypedFormArray {
    return this.form.get('slides') as UntypedFormArray
  }

  get buttons(): UntypedFormArray {
    return this.form.get('buttons') as UntypedFormArray
  }

  public getButtonGroup(index: number): UntypedFormGroup {
    return this.buttons.at(index) as UntypedFormGroup
  }

  public addSlide(slide?: HomePageSlide): void {
    if (this.slides.length >= this.maxSlides) {
      return
    }

    this.slides.push(this.createSlideGroup(slide))
    this.slideCollapsed.push(Boolean(slide))
    this.slideLanguageSelected.push('en')
    this.syncSlideOrder()
  }

  public removeSlide(index: number): void {
    this.slides.removeAt(index)
    this.slideCollapsed.splice(index, 1)
    this.slideLanguageSelected.splice(index, 1)
    this.syncSlideOrder()
  }

  public dropSlide(event: CdkDragDrop<AbstractControl[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return
    }

    const control = this.slides.at(event.previousIndex)
    this.slides.removeAt(event.previousIndex)
    this.slides.insert(event.currentIndex, control)
    moveItemInArray(
      this.slideCollapsed,
      event.previousIndex,
      event.currentIndex
    )
    moveItemInArray(
      this.slideLanguageSelected,
      event.previousIndex,
      event.currentIndex
    )
    this.syncSlideOrder()
  }

  public addButton(button?: HomePageButton): void {
    this.buttons.push(this.createButtonGroup(button))
    this.buttonCollapsed.push(Boolean(button))
    this.syncButtonOrder()
  }

  public removeButton(index: number): void {
    this.buttons.removeAt(index)
    this.buttonCollapsed.splice(index, 1)
    this.syncButtonOrder()
  }

  public dropButton(event: CdkDragDrop<AbstractControl[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return
    }

    const control = this.buttons.at(event.previousIndex)
    this.buttons.removeAt(event.previousIndex)
    this.buttons.insert(event.currentIndex, control)
    moveItemInArray(
      this.buttonCollapsed,
      event.previousIndex,
      event.currentIndex
    )
    this.syncButtonOrder()
  }

  public getIconClass(icon: string | null | undefined): string {
    const normalizedIcon = this.normalizeIcon(icon)
    return normalizedIcon ? `fa-${normalizedIcon}` : ''
  }

  public setSlideLanguage(index: number, language: LanguageCode): void {
    this.slideLanguageSelected[index] = language
  }

  public getSlideLanguage(index: number): LanguageCode {
    return this.slideLanguageSelected[index] ?? 'en'
  }

  public hasMissingSlideLanguageFields(
    slideIndex: number,
    language: LanguageCode
  ): boolean {
    const slide = this.slides.at(slideIndex)

    return (
      !String(slide.get(this.getImageUrlKey(language))?.value ?? '').trim() ||
      !String(slide.get(this.getAltTextKey(language))?.value ?? '').trim()
    )
  }

  public getImageUrl(imagePath: string | null | undefined): string {
    if (!imagePath) {
      return ''
    }

    if (/^(https?:)?\/\//.test(imagePath)) {
      return imagePath
    }

    return `${this.staticAssetBaseUrl}${imagePath.replace(/^\/+/, '')}`
  }

  public onSubmitSlideImage(file: File, slideIndex: number): void {
    const language = this.getSlideLanguage(slideIndex)
    const path = `branding/${uuidv4()}`

    this.store.dispatch(
      documentActions.createOneWithFile({
        entity: {
          type: DocumentType.BRANDING_IMAGE,
          isPublic: true,
          path
        },
        file
      })
    )

    this.actions$
      .pipe(
        ofType(documentActions.upsertMany),
        take(1),
        timeout(5000),
        catchError(() => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          return this.notificationService.emit('alerts.warning', 'error')
        }

        const document: Document = action.entities[0]
        this.slides
          .at(slideIndex)
          .get(this.getImageUrlKey(language))
          ?.setValue(document.path)
        this.notificationService.emit('alerts.success', 'success')
      })
  }

  public onDeleteSlideImage(slideIndex: number): void {
    const language = this.getSlideLanguage(slideIndex)
    this.slides
      .at(slideIndex)
      .get(this.getImageUrlKey(language))
      ?.setValue(null)
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      this.markDirty(this.form)
    }

    if (this.form.invalid) {
      return
    }

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(1000),
        catchError(() => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )

    this.configOptionService.updateOrCreateMany({
      [ConfigOptionsKeys.HOME_PAGE_CONFIG]: this.buildPayload()
    })
  }

  private createButtonGroup(button?: HomePageButton): UntypedFormGroup {
    return this.fb.group({
      ...this.buildTitleControls(button?.title),
      url: [button?.url ?? null, [Validators.required]],
      icon: [button?.icon ?? null, [Validators.required]],
      style: [
        button?.style ?? HomePageButtonStyle.PRIMARY,
        [Validators.required]
      ],
      order: [
        button?.order ?? this.buttons.length + 1,
        [Validators.required, WholeNumberValidator(), Validators.min(0)]
      ]
    })
  }

  private createSlideGroup(slide?: HomePageSlide): UntypedFormGroup {
    return this.fb.group({
      ...this.buildSlideControls(slide),
      url: [slide?.url ?? null, []],
      order: [
        slide?.order ?? this.slides.length + 1,
        [Validators.required, WholeNumberValidator(), Validators.min(0)]
      ]
    })
  }

  private buildPayload(): HomePageConfigWebOption {
    const rawValue = this.form.getRawValue()

    return {
      title: this.buildLocalizedValue(rawValue),
      slides: (rawValue.slides ?? []).map((slide, index) =>
        this.normalizeSlide(slide, index)
      ),
      buttons: (rawValue.buttons ?? []).map((button, index) =>
        this.normalizeButton(button, index)
      )
    }
  }

  private normalizeSlide(slide: any, index: number): HomePageSlide {
    return this.buildSlidePayload(slide, index)
  }

  private normalizeButton(button: any, index: number): HomePageButton {
    return {
      title: this.buildLocalizedValue(button),
      url: String(button.url ?? '').trim(),
      icon: this.normalizeIcon(button.icon),
      style: button.style ?? HomePageButtonStyle.PRIMARY,
      order: index + 1
    }
  }

  private normalizeIcon(icon: string | null | undefined): string {
    return String(icon ?? '')
      .trim()
      .replace(/^fa\s+/, '')
      .replace(/^fa-/, '')
  }

  private buildTitleControls(
    source: HomePageLocalizedValue | null | undefined
  ): Record<TitleKey, any> {
    return this.languageCodes.reduce(
      (controls, language) => {
        const key = this.getTitleKey(language)
        controls[key] = [source?.[language] ?? null, []]
        return controls
      },
      {} as Record<TitleKey, any>
    )
  }

  private buildTitleFormValue(
    source: HomePageLocalizedValue | null | undefined
  ): Record<TitleKey, string> {
    return this.languageCodes.reduce(
      (titles, language) => {
        const key = this.getTitleKey(language)
        titles[key] = source?.[language] ?? ''
        return titles
      },
      {} as Record<TitleKey, string>
    )
  }

  private buildLocalizedValue(
    source: TitleSource | SlideFormSource | null | undefined,
    prefix: 'title' | 'imageUrl' | 'altText' = 'title'
  ): HomePageLocalizedValue {
    return this.languageCodes.reduce(
      (localizedValue, language) => {
        const key = `${prefix}_${language}` as
          | TitleKey
          | ImageUrlKey
          | AltTextKey
        localizedValue[language] = String(source?.[key] ?? '').trim()
        return localizedValue
      },
      {} as HomePageLocalizedValue
    )
  }

  private getTitleKey(language: LanguageCode): TitleKey {
    return `title_${language}` as TitleKey
  }

  private buildSlideControls(
    source: HomePageSlide | null | undefined
  ): Record<ImageUrlKey | AltTextKey, any> {
    return this.languageCodes.reduce(
      (controls, language) => {
        const imageUrlKey = this.getImageUrlKey(language)
        const altTextKey = this.getAltTextKey(language)
        controls[imageUrlKey] = [source?.imageUrl?.[language] ?? null, []]
        controls[altTextKey] = [source?.altText?.[language] ?? null, []]
        return controls
      },
      {} as Record<ImageUrlKey | AltTextKey, any>
    )
  }

  private buildSlidePayload(
    source: SlideFormSource | null | undefined,
    index: number
  ): HomePageSlide {
    return {
      imageUrl: this.buildLocalizedValue(source, 'imageUrl'),
      altText: this.buildLocalizedValue(source, 'altText'),
      url: String(source?.url ?? '').trim(),
      order: index + 1
    }
  }

  public getImageUrlKey(language: LanguageCode): ImageUrlKey {
    return `imageUrl_${language}` as ImageUrlKey
  }

  public getAltTextKey(language: LanguageCode): AltTextKey {
    return `altText_${language}` as AltTextKey
  }

  private syncSlideOrder(): void {
    this.slides.controls.forEach((slide, index) => {
      slide.get('order')?.setValue(index + 1, { emitEvent: false })
    })
  }

  private syncButtonOrder(): void {
    this.buttons.controls.forEach((button, index) => {
      button.get('order')?.setValue(index + 1, { emitEvent: false })
    })
  }

  private markDirty(control: AbstractControl): void {
    if (control instanceof UntypedFormControl) {
      control.markAsDirty()
      return
    }

    if (control instanceof UntypedFormArray) {
      control.controls.forEach((child) => this.markDirty(child))
      return
    }

    if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach((child) => this.markDirty(child))
    }
  }
}
